import { ThemeProvider, CssBaseline, useMediaQuery } from "@mui/material";
import { theme as makeTheme } from "../theme/muiThemeDefault";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Choice, ColorModeContextValue, Mode } from "src/types";
import { STORAGE_KEY } from "../constants";

export const ColorModeContext = createContext<ColorModeContextValue | null>(
  null
);

export const ColorModeProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)", {
    noSsr: true,
  });

  const [choice, setChoice] = useState<Choice>(() => {
    const saved =
      typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    return (saved as Choice) || "system";
  });

  const resolved: Mode = useMemo(() => {
    if (choice === "system") return prefersDark ? "dark" : "light";
    return choice;
  }, [choice, prefersDark]);

  // persist choice
  useEffect(() => {
    if (typeof window !== "undefined")
      localStorage.setItem(STORAGE_KEY, choice);
  }, [choice]);

  // create MUI theme from your factory
  const muiTheme = useMemo(() => makeTheme(resolved), [resolved]);

  const value = useMemo<ColorModeContextValue>(
    () => ({
      mode: resolved,
      choice,
      setChoice,
      toggle: () => setChoice((c) => (c === "dark" ? "light" : "dark")),
    }),
    [resolved, choice]
  );

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

// Convenience hook
export const useColorMode = () => {
  const ctx = useContext(ColorModeContext);
  if (!ctx)
    throw new Error("useColorMode must be used within ColorModeProvider");
  return ctx;
};
